import {
  type Cause,
  Context,
  Effect,
  Layer,
  Schema,
  SchemaGetter,
} from 'effect'

export class ReportingUser extends Schema.Class<ReportingUser>('ReportingUser')(
  {
    id: Schema.Union([Schema.String, Schema.Number]),
    email: Schema.String.pipe(Schema.optionalKey),
    name: Schema.String.pipe(Schema.optionalKey),
  },
) {}

export class SentryReportingUser extends Schema.Class<SentryReportingUser>(
  'SentryReportingUser',
)({
  id: Schema.Union([Schema.String, Schema.Number]).pipe(Schema.optionalKey),
  email: Schema.String.pipe(Schema.optionalKey),
  username: Schema.String.pipe(Schema.optionalKey),
  ip_address: Schema.NullOr(Schema.String).pipe(Schema.optionalKey),
  geo: Schema.Struct({
    country_code: Schema.String.pipe(Schema.optionalKey),
    region: Schema.String.pipe(Schema.optionalKey),
    city: Schema.String.pipe(Schema.optionalKey),
  }).pipe(Schema.optionalKey),
}) {
  private static readonly SentryUserFromUser = ReportingUser.pipe(
    Schema.decodeTo(SentryReportingUser, {
      decode: SchemaGetter.transform((reportingUser: ReportingUser) => ({
        id: reportingUser.id,
        ...(reportingUser.email && { email: reportingUser.email }),
        ...(reportingUser.name && { username: reportingUser.name }),
      })),
      encode: SchemaGetter.transform(
        (sentryReportingUser: {
          readonly id?: string | number
          readonly email?: string
          readonly username?: string
          readonly ip_address?: string | null
          readonly geo?: {
            readonly country_code?: string
            readonly region?: string
            readonly city?: string
          }
        }) => ({
          id: sentryReportingUser.id ?? -1,
          ...(sentryReportingUser.email && {
            email: sentryReportingUser.email,
          }),
          ...(sentryReportingUser.username && {
            name: sentryReportingUser.username,
          }),
        }),
      ),
    }),
  )

  static readonly fromReporting = Schema.decodeUnknownSync(
    SentryReportingUser.SentryUserFromUser,
  )
}

export interface CrashReporterShape {
  init(): Effect.Effect<void>
  setUser(user: ReportingUser): Effect.Effect<void>
  report<E = unknown>(
    error: Error | Cause.Cause<E>,
    additionalInfo?: Record<string, unknown>,
  ): Effect.Effect<void>
}

export class CrashReporter extends Context.Service<
  CrashReporter,
  CrashReporterShape
>()('CrashReporter') {
  static readonly forTest = (implementation?: Partial<CrashReporterShape>) =>
    Layer.succeed(
      this,
      CrashReporter.of({
        init: () => Effect.void,
        setUser: () => Effect.void,
        report: () => Effect.void,
        ...implementation,
      }),
    )
}
